import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  CircularProgress,
  Alert,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Chip,
  Tooltip,
  Checkbox,
  FormControlLabel,
  useTheme,
  useMediaQuery,
  Fab,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  AutoAwesome as EnhancedIcon,
  Restore as RestoredIcon,
  DeleteSweep as DeleteSweepIcon,
  Fullscreen as FullscreenIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  Close as CloseIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
  Storage as StorageIcon,
  PhoneAndroid as DeviceIcon,
  Info as InfoIcon,
  Download as DownloadIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import CountryHeatmap from '../../components/CountryHeatmap';

interface OutputImage {
  filename: string;
  url: string;
  size: number;
  sizeFormatted: string;
  created: string;
  modified: string;
  createdFormatted: string;
  modifiedFormatted: string;
  type: 'image' | 'video' | 'enhanced' | 'restored' | 'result';
  isVideo: boolean;
  deviceId?: string | null;
  operationType?: string | null;
  timestamp?: number | null;
  userId?: string | null;
  userEmail?: string | null;
  platform?: string | null;
  templateId?: string | null;
  ipAddress?: string | null;
  country?: string | null;
  countryCode?: string | null;
  metadata?: any;
}

interface OutputImagesResponse {
  images: OutputImage[];
  total: number;
  page: number;
  limit: number | 'all';
  totalPages: number;
  totalStorage?: number;
  totalStorageFormatted?: string;
  deviceIds?: string[];
  userIds?: string[];
  templateIds?: string[];
  countries?: string[];
  countryCounts?: Array<{ country: string; count: number }>;
}

const OutputImages: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<number | 'all'>(isMobile ? 12 : 24);
  const [typeFilter, setTypeFilter] = useState<'all' | 'images' | 'videos'>('all');
  const [deviceIdFilter, setDeviceIdFilter] = useState<string>('');
  const [userIdFilter, setUserIdFilter] = useState<string>('');
  const [templateIdFilter, setTemplateIdFilter] = useState<string>('');
  const [countryFilter, setCountryFilter] = useState<string>('');
  const [showHeatmap, setShowHeatmap] = useState<boolean>(() => {
    const saved = localStorage.getItem('outputImagesShowHeatmap');
    return saved !== null ? saved === 'true' : false;
  });
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteSingleDialogOpen, setDeleteSingleDialogOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<string | null>(null);
  const [quickDeleteMinutes, setQuickDeleteMinutes] = useState<number | null>(null);
  const [quickDeleteDialogOpen, setQuickDeleteDialogOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [previewIndex, setPreviewIndex] = useState<number>(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [blurEnabled, setBlurEnabled] = useState<boolean>(() => {
    // Default to blur ON, check localStorage for saved preference
    const saved = localStorage.getItem('outputImagesBlurEnabled');
    return saved !== null ? saved === 'true' : true;
  });
  const [previewBlurDisabled, setPreviewBlurDisabled] = useState<boolean>(false);
  const [metadataDialogOpen, setMetadataDialogOpen] = useState<boolean>(false);
  const [selectedImageMetadata, setSelectedImageMetadata] = useState<OutputImage | null>(null);
  const [videoDimensions, setVideoDimensions] = useState<{ width: number; height: number } | null>(null);
  const [videoCodecIssue, setVideoCodecIssue] = useState<boolean>(false);
  const [videoLoadErrors, setVideoLoadErrors] = useState<Set<string>>(new Set());
  const [previewVideoError, setPreviewVideoError] = useState<boolean>(false);
  const [videoPlaybackMethod, setVideoPlaybackMethod] = useState<'video' | 'iframe' | 'object' | 'error'>('video');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    const saved = localStorage.getItem('outputImagesViewMode');
    return (saved === 'grid' || saved === 'list') ? saved : 'grid';
  });
  const [thumbnailSize, setThumbnailSize] = useState<'small' | 'medium' | 'large'>(() => {
    const saved = localStorage.getItem('outputImagesThumbnailSize');
    return (saved === 'small' || saved === 'medium' || saved === 'large') ? saved : 'medium';
  });
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoPlaybackMethodRef = useRef<'video' | 'iframe' | 'object' | 'error'>('video');
  const queryClient = useQueryClient();

  // Update limit when screen size changes (only if not set to 'all' and not a custom value)
  useEffect(() => {
    // Only auto-adjust if limit is one of the default values (12, 18, 24)
    // This prevents resetting user-selected custom limits
    if (limit !== 'all' && (limit === 12 || limit === 18 || limit === 24)) {
      if (isMobile) {
        setLimit(12);
      } else if (isTablet) {
        setLimit(18);
      } else {
        setLimit(24);
      }
    }
  }, [isMobile, isTablet]);

  // Fetch output images
  const { data, isLoading, error, refetch } = useQuery<OutputImagesResponse>({
    queryKey: ['outputImages', page, limit, typeFilter, deviceIdFilter, userIdFilter, templateIdFilter, countryFilter],
    queryFn: async () => {
      // Pass limit as-is (backend handles 'all')
      const response = await apiService.getOutputImages(
        page, 
        limit, 
        typeFilter,
        deviceIdFilter || undefined,
        userIdFilter || undefined,
        templateIdFilter || undefined,
        countryFilter || undefined
      );
      return response.data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Delete single image mutation
  const deleteImageMutation = useMutation({
    mutationFn: async (filename: string) => {
      const response = await apiService.deleteOutputImage(filename);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['outputImages'] });
      setDeleteSingleDialogOpen(false);
      setImageToDelete(null);
      setSelectedImages(new Set());
    },
  });

  // Delete multiple images mutation
  const deleteMultipleMutation = useMutation({
    mutationFn: async (filenames: string[]) => {
      const response = await apiService.deleteMultipleOutputImages(filenames);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['outputImages'] });
      setDeleteDialogOpen(false);
      setSelectedImages(new Set());
    },
  });

  // Delete images older than X minutes mutation
  const deleteOlderThanMutation = useMutation({
    mutationFn: async (minutes: number) => {
      const response = await apiService.deleteOutputImagesOlderThan(minutes);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['outputImages'] });
      setQuickDeleteDialogOpen(false);
      setQuickDeleteMinutes(null);
    },
  });

  const handleQuickDelete = (minutes: number) => {
    setQuickDeleteMinutes(minutes);
    setQuickDeleteDialogOpen(true);
  };

  const handleQuickDeleteConfirm = () => {
    if (quickDeleteMinutes !== null) {
      deleteOlderThanMutation.mutate(quickDeleteMinutes);
    }
  };

  const formatQuickDeleteLabel = (minutes: number) => {
    if (minutes < 60) return `${minutes} min`;
    if (minutes < 1440) return `${minutes / 60} hour${minutes / 60 > 1 ? 's' : ''}`;
    return `${minutes / 1440} day${minutes / 1440 > 1 ? 's' : ''}`;
  };

  const handleDeleteClick = (filename: string) => {
    setImageToDelete(filename);
    setDeleteSingleDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (imageToDelete) {
      deleteImageMutation.mutate(imageToDelete);
    }
  };

  const handleDeleteMultiple = () => {
    if (selectedImages.size > 0) {
      deleteMultipleMutation.mutate(Array.from(selectedImages));
    }
  };

  const handleSelectImage = (filename: string) => {
    const newSelected = new Set(selectedImages);
    if (newSelected.has(filename)) {
      newSelected.delete(filename);
    } else {
      newSelected.add(filename);
    }
    setSelectedImages(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedImages.size === data?.images.length) {
      setSelectedImages(new Set());
    } else {
      setSelectedImages(new Set(data?.images.map(img => img.filename) || []));
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'video':
        return <VideoIcon />;
      case 'enhanced':
        return <EnhancedIcon />;
      case 'restored':
        return <RestoredIcon />;
      default:
        return <ImageIcon />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'video':
        return 'error';
      case 'enhanced':
        return 'primary';
      case 'restored':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  // Swipe handlers for mobile
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && data && previewIndex < data.images.length - 1) {
      handleNextImage();
    }
    if (isRightSwipe && previewIndex > 0) {
      handlePreviousImage();
    }
  };

  const handleImageClick = (imageUrl: string, index: number) => {
    setPreviewImage(imageUrl);
    setPreviewIndex(index);
  };

  const handleNextImage = () => {
    if (data && previewIndex < data.images.length - 1) {
      const nextIndex = previewIndex + 1;
      setPreviewIndex(nextIndex);
      setPreviewImage(data.images[nextIndex].url);
    } else if (data && page < data.totalPages) {
      // Load next page
      setPage(page + 1);
    }
  };

  const handlePreviousImage = () => {
    if (previewIndex > 0) {
      const prevIndex = previewIndex - 1;
      setPreviewIndex(prevIndex);
      setPreviewImage(data!.images[prevIndex].url);
    } else if (page > 1) {
      // Load previous page
      setPage(page - 1);
    }
  };

  // Update preview index when page changes
  useEffect(() => {
    if (previewImage && data) {
      const currentIndex = data.images.findIndex(img => img.url === previewImage);
      if (currentIndex !== -1) {
        setPreviewIndex(currentIndex);
      }
    }
  }, [data, previewImage]);

  // Save blur preference to localStorage
  useEffect(() => {
    localStorage.setItem('outputImagesBlurEnabled', blurEnabled.toString());
  }, [blurEnabled]);

  // Save view mode preference
  useEffect(() => {
    localStorage.setItem('outputImagesViewMode', viewMode);
  }, [viewMode]);

  // Save thumbnail size preference
  useEffect(() => {
    localStorage.setItem('outputImagesThumbnailSize', thumbnailSize);
  }, [thumbnailSize]);

  // Save heatmap visibility preference
  useEffect(() => {
    localStorage.setItem('outputImagesShowHeatmap', showHeatmap.toString());
  }, [showHeatmap]);

  // Reset preview blur when dialog closes
  useEffect(() => {
    if (!previewImage) {
      setPreviewBlurDisabled(false);
      setVideoDimensions(null);
      setVideoCodecIssue(false);
      setPreviewVideoError(false);
      setVideoPlaybackMethod('video');
      videoPlaybackMethodRef.current = 'video';
    } else {
      // Reset error state when preview image changes
      setPreviewVideoError(false);
      setVideoPlaybackMethod('video');
      videoPlaybackMethodRef.current = 'video';
    }
  }, [previewImage]);

  // Sync ref with state
  useEffect(() => {
    videoPlaybackMethodRef.current = videoPlaybackMethod;
  }, [videoPlaybackMethod]);

  // Clear video load errors when data changes (new page/filter)
  useEffect(() => {
    setVideoLoadErrors(new Set());
  }, [data]);

  // Auto-play video when it's ready and handle video switching
  useEffect(() => {
    if (previewImage && videoRef.current && data?.images[previewIndex]?.isVideo) {
      const video = videoRef.current;
      
      // Reset video when URL changes
      if (video.src !== previewImage) {
        video.load();
      }
      
      // Try to play when video is ready
      const tryPlay = () => {
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log('Video autoplay started');
            })
            .catch((error) => {
              console.log('Video autoplay prevented (user interaction may be required):', error);
            });
        }
      };
      
      // Try to play immediately if video is already loaded
      if (video.readyState >= 2) {
        tryPlay();
      } else {
        // Wait for video to be ready
        video.addEventListener('canplay', tryPlay, { once: true });
      }
      
      return () => {
        video.removeEventListener('canplay', tryPlay);
      };
    }
    
    // Pause video when preview closes or switches
    return () => {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    };
  }, [previewImage, previewIndex, data]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle shortcuts when not in input/textarea
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      // Delete key - delete selected images or current preview
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (e.ctrlKey || e.metaKey) {
          // Ctrl/Cmd + Delete: Delete selected images
          if (selectedImages.size > 0) {
            e.preventDefault();
            setDeleteDialogOpen(true);
          }
        } else if (previewImage && data?.images[previewIndex]) {
          // Delete current preview image
          e.preventDefault();
          handleDeleteClick(data.images[previewIndex].filename);
        }
      }

      // Arrow keys - navigate preview
      if (previewImage) {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          e.preventDefault();
          if (e.key === 'ArrowLeft') {
            handlePreviousImage();
          } else {
            handleNextImage();
          }
        }
        // Escape - close preview
        if (e.key === 'Escape') {
          e.preventDefault();
          setPreviewImage(null);
        }
      }

      // Arrow keys in grid/list view
      if (!previewImage && data && data.images.length > 0) {
        if (e.key === 'ArrowLeft' && page > 1) {
          e.preventDefault();
          setPage(page - 1);
        } else if (e.key === 'ArrowRight' && page < data.totalPages) {
          e.preventDefault();
          setPage(page + 1);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewImage, previewIndex, selectedImages.size, data, page, handleDeleteClick, handlePreviousImage, handleNextImage]);

  if (isLoading && !data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load output images. Please try again.
      </Alert>
    );
  }

  // Get thumbnail size dimensions
  const getThumbnailHeight = () => {
    if (viewMode === 'list') return 60;
    switch (thumbnailSize) {
      case 'small': return isMobile ? 100 : 120;
      case 'large': return isMobile ? 180 : 220;
      default: return isMobile ? 140 : 180;
    }
  };

  // Get grid columns based on thumbnail size
  const getGridColumns = () => {
    if (viewMode === 'list') return { xs: 12 };
    switch (thumbnailSize) {
      case 'small': return { xs: 6, sm: 4, md: 3, lg: 4, xl: 5 };
      case 'large': return { xs: 12, sm: 6, md: 4, lg: 3, xl: 2 };
      default: return { xs: 6, sm: 4, md: 3, lg: 2 };
    }
  };

  return (
    <Box sx={{ p: isMobile ? 1 : 2 }}>
      {/* Statistics Bar */}
      {data && (
        <Box 
          display="flex" 
          gap={2} 
          mb={2} 
          flexWrap="wrap"
          alignItems="center"
          sx={{
            p: 2,
            bgcolor: 'background.paper',
            borderRadius: 1,
            boxShadow: 1,
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <ImageIcon color="primary" />
            <Typography variant="body2" color="text.secondary">
              Total Images:
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {data.total}
            </Typography>
          </Box>
          {data.totalStorageFormatted && (
            <Box display="flex" alignItems="center" gap={1}>
              <StorageIcon color="primary" />
              <Typography variant="body2" color="text.secondary">
                Total Storage:
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {data.totalStorageFormatted}
              </Typography>
            </Box>
          )}
          {data.deviceIds && data.deviceIds.length > 0 && (
            <Box display="flex" alignItems="center" gap={1}>
              <DeviceIcon color="primary" />
              <Typography variant="body2" color="text.secondary">
                Unique Devices:
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {data.deviceIds.length}
              </Typography>
            </Box>
          )}
          {data.userIds && data.userIds.length > 0 && (
            <Box display="flex" alignItems="center" gap={1}>
              <ImageIcon color="primary" />
              <Typography variant="body2" color="text.secondary">
                Unique Users:
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {data.userIds.length}
              </Typography>
            </Box>
          )}
          {data.countries && data.countries.length > 0 && (
            <Box display="flex" alignItems="center" gap={1}>
              <ImageIcon color="primary" />
              <Typography variant="body2" color="text.secondary">
                Unique Countries:
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {data.countries.length}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Country Heatmap Toggle and Display */}
      {data?.countryCounts && data.countryCounts.length > 0 && (
        <Box mb={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Country Distribution Heatmap
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showHeatmap}
                  onChange={(e) => setShowHeatmap(e.target.checked)}
                  size={isMobile ? 'small' : 'medium'}
                />
              }
              label={
                <Typography variant={isMobile ? 'body2' : 'body1'}>
                  Show Heatmap
                </Typography>
              }
            />
          </Box>
          {showHeatmap && (
            <CountryHeatmap countryCounts={data.countryCounts} />
          )}
        </Box>
      )}

      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems={isMobile ? 'flex-start' : 'center'} 
        mb={2}
        flexDirection={isMobile ? 'column' : 'row'}
        gap={isMobile ? 2 : 0}
      >
        <Typography variant={isMobile ? 'h5' : 'h4'} component="h1">
          Output Images
        </Typography>
        <Box 
          display="flex" 
          gap={1} 
          alignItems="center" 
          flexWrap="wrap"
          width={isMobile ? '100%' : 'auto'}
        >
          {/* View Mode Toggle */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => {
              if (newMode !== null) setViewMode(newMode);
            }}
            size="small"
            aria-label="view mode"
          >
            <ToggleButton value="grid" aria-label="grid view">
              <GridViewIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="list" aria-label="list view">
              <ListViewIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Type Filter Toggle (Images/Videos) */}
          <ToggleButtonGroup
            value={typeFilter}
            exclusive
            onChange={(_, newFilter) => {
              if (newFilter !== null) {
                setTypeFilter(newFilter);
                setPage(1);
              }
            }}
            size="small"
            aria-label="type filter"
          >
            <ToggleButton value="all" aria-label="all types">
              <Typography variant="caption" sx={{ px: 0.5 }}>All</Typography>
            </ToggleButton>
            <ToggleButton value="images" aria-label="images only">
              <ImageIcon fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="caption">Images</Typography>
            </ToggleButton>
            <ToggleButton value="videos" aria-label="videos only">
              <VideoIcon fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="caption">Videos</Typography>
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Thumbnail Size Selector (only in grid view) */}
          {viewMode === 'grid' && (
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Size</InputLabel>
              <Select
                value={thumbnailSize}
                label="Size"
                onChange={(e) => setThumbnailSize(e.target.value as 'small' | 'medium' | 'large')}
              >
                <MenuItem value="small">Small</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="large">Large</MenuItem>
              </Select>
            </FormControl>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={blurEnabled}
                onChange={(e) => setBlurEnabled(e.target.checked)}
                size={isMobile ? 'small' : 'medium'}
              />
            }
            label={
              <Typography variant={isMobile ? 'body2' : 'body1'}>
                Blur Images
              </Typography>
            }
            sx={{ mr: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 150 }}>
            <InputLabel>Device ID</InputLabel>
            <Select
              value={deviceIdFilter}
              label="Device ID"
              onChange={(e) => {
                setDeviceIdFilter(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="">All Devices</MenuItem>
              {data?.deviceIds?.map((deviceId) => (
                <MenuItem key={deviceId} value={deviceId}>
                  {deviceId.length > 20 ? `${deviceId.substring(0, 20)}...` : deviceId}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 150 }}>
            <InputLabel>User ID</InputLabel>
            <Select
              value={userIdFilter}
              label="User ID"
              onChange={(e) => {
                setUserIdFilter(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="">All Users</MenuItem>
              {data?.userIds?.map((userId) => (
                <MenuItem key={userId} value={userId}>
                  {userId.length > 20 ? `${userId.substring(0, 20)}...` : userId}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 150 }}>
            <InputLabel>Template ID</InputLabel>
            <Select
              value={templateIdFilter}
              label="Template ID"
              onChange={(e) => {
                setTemplateIdFilter(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="">All Templates</MenuItem>
              {data?.templateIds?.map((templateId) => (
                <MenuItem key={templateId} value={templateId}>
                  {templateId.length > 20 ? `${templateId.substring(0, 20)}...` : templateId}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 150 }}>
            <InputLabel>Country</InputLabel>
            <Select
              value={countryFilter}
              label="Country"
              onChange={(e) => {
                setCountryFilter(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="">All Countries</MenuItem>
              {data?.countries?.map((country) => (
                <MenuItem key={country} value={country}>
                  {country}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 100 }}>
            <InputLabel>Per Page</InputLabel>
            <Select
              value={limit}
              label="Per Page"
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'all') {
                  setLimit('all');
                } else {
                  setLimit(Number(value));
                }
                setPage(1);
              }}
            >
              <MenuItem value={12}>12</MenuItem>
              <MenuItem value={24}>24</MenuItem>
              <MenuItem value={48}>48</MenuItem>
              <MenuItem value={96}>96</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>
          <IconButton onClick={() => refetch()} disabled={isLoading} size={isMobile ? 'small' : 'medium'}>
            <RefreshIcon />
          </IconButton>
          {selectedImages.size > 0 && (
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteSweepIcon />}
              onClick={() => setDeleteDialogOpen(true)}
              size={isMobile ? 'small' : 'medium'}
              fullWidth={isMobile}
            >
              {isMobile ? `Delete (${selectedImages.size})` : `Delete Selected (${selectedImages.size})`}
            </Button>
          )}
        </Box>
      </Box>

      {/* Quick Delete Section */}
      <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
        <Typography variant="body2" fontWeight={600} color="text.secondary">
          Quick Delete:
        </Typography>
        {[
          { label: 'Delete > 10 min', minutes: 10 },
          { label: 'Delete > 1 hour', minutes: 60 },
          { label: 'Delete > 24 hours', minutes: 1440 },
          { label: 'Delete > 7 days', minutes: 10080 },
        ].map(({ label, minutes }) => (
          <Button
            key={minutes}
            variant="outlined"
            color="error"
            size="small"
            onClick={() => handleQuickDelete(minutes)}
            disabled={deleteOlderThanMutation.isPending}
          >
            {label}
          </Button>
        ))}
      </Box>

      {data && data.images.length === 0 ? (
        <Alert severity="info">No output images found.</Alert>
      ) : (
        <>
          {data && data.images.length > 0 && (
            <Box mb={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedImages.size === data.images.length && data.images.length > 0}
                    indeterminate={selectedImages.size > 0 && selectedImages.size < data.images.length}
                    onChange={handleSelectAll}
                    size={isMobile ? 'small' : 'medium'}
                  />
                }
                label={
                  <Typography variant={isMobile ? 'body2' : 'body1'}>
                    Select All ({selectedImages.size} selected)
                  </Typography>
                }
              />
            </Box>
          )}

          {/* List View */}
          {viewMode === 'list' ? (
            <TableContainer component={Paper} sx={{ maxHeight: '70vh' }}>
              <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" width={50}>
                      <Checkbox
                        checked={selectedImages.size === data?.images.length && (data?.images.length ?? 0) > 0}
                        indeterminate={selectedImages.size > 0 && selectedImages.size < (data?.images.length ?? 0)}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell width={80}>Preview</TableCell>
                    <TableCell>Filename</TableCell>
                    <TableCell>Device/User Info</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Size</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="center" width={120}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.images.map((image, index) => (
                    <TableRow
                      key={image.filename}
                      hover
                      sx={{
                        cursor: 'pointer',
                        bgcolor: selectedImages.has(image.filename) ? 'action.selected' : 'inherit',
                      }}
                      onClick={() => handleImageClick(image.url, index)}
                    >
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedImages.has(image.filename)}
                          onChange={() => handleSelectImage(image.filename)}
                        />
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'grey.100',
                            borderRadius: 1,
                            overflow: 'hidden',
                            position: 'relative',
                          }}
                          onClick={() => handleImageClick(image.url, index)}
                        >
                          {image.isVideo ? (
                            videoLoadErrors.has(image.url) ? (
                              <Box
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: 'grey.200',
                                  color: 'text.secondary',
                                }}
                              >
                                <VideoIcon sx={{ fontSize: 24, mb: 0.5, opacity: 0.5 }} />
                                <Typography variant="caption" sx={{ fontSize: '0.6rem', textAlign: 'center', px: 0.5 }}>
                                  Video
                                </Typography>
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  position: 'relative',
                                }}
                              >
                                <Box
                                  component="video"
                                  src={image.url}
                                  preload="metadata"
                                  muted
                                  playsInline
                                  crossOrigin="anonymous"
                                  width="100%"
                                  height="100%"
                                  style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain',
                                    filter: blurEnabled ? 'blur(8px)' : 'none',
                                    display: 'block',
                                  }}
                                  onError={(e: any) => {
                                    // Silently handle video load errors - mark as failed
                                    setVideoLoadErrors(prev => new Set(prev).add(image.url));
                                  }}
                                  onLoadedMetadata={(e: any) => {
                                    const video = e.target as HTMLVideoElement;
                                    try {
                                      // Seek to first frame to show thumbnail
                                      if (video.duration > 0) {
                                        video.currentTime = 0.01;
                                      }
                                    } catch (err) {
                                      // Ignore seek errors
                                    }
                                  }}
                                />
                              </Box>
                            )
                          ) : (
                            <Box
                              component="img"
                            src={image.url}
                            alt={image.filename}
                            sx={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                              filter: blurEnabled ? 'blur(8px)' : 'none',
                              transition: 'filter 0.3s ease',
                            }}
                            onError={(e: any) => {
                              e.target.src = '/placeholder-image.png';
                            }}
                          />
                          )}
                          <Chip
                            icon={getTypeIcon(image.type)}
                            label=""
                            size="small"
                            color={getTypeColor(image.type) as any}
                            sx={{
                              position: 'absolute',
                              top: 2,
                              right: 2,
                              height: 16,
                              '& .MuiChip-icon': {
                                fontSize: 12,
                                margin: 0,
                              },
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                          {image.filename}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          {image.deviceId && image.deviceId !== 'unknown' ? (
                            <Chip
                              label={image.deviceId}
                              size="small"
                              variant="outlined"
                              color="primary"
                              sx={{ fontFamily: 'monospace', fontSize: '0.7rem', mb: 0.5 }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              N/A
                            </Typography>
                          )}
                          {image.userId && (
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.65rem' }}>
                              User: {image.userId.substring(0, 12)}...
                            </Typography>
                          )}
                          {image.userEmail && (
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                              {image.userEmail}
                            </Typography>
                          )}
                          {image.platform && (
                            <Chip
                              label={image.platform}
                              size="small"
                              variant="outlined"
                              sx={{ mt: 0.5, fontSize: '0.65rem', height: 18 }}
                            />
                          )}
                          {image.templateId && (
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.65rem', fontFamily: 'monospace' }}>
                              Template: {image.templateId}
                            </Typography>
                          )}
                          {(image.country || image.countryCode) && (
                            <Chip
                              label={image.country || image.countryCode || 'Unknown'}
                              size="small"
                              variant="outlined"
                              color="secondary"
                              sx={{ mt: 0.5, fontSize: '0.65rem', height: 18 }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getTypeIcon(image.type)}
                          label={image.type}
                          size="small"
                          color={getTypeColor(image.type) as any}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{image.sizeFormatted}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(image.createdFormatted)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <Tooltip title="View">
                            <IconButton
                              size="small"
                              onClick={() => handleImageClick(image.url, index)}
                            >
                              <FullscreenIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Metadata">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedImageMetadata(image);
                                setMetadataDialogOpen(true);
                              }}
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(image.filename)}
                              disabled={deleteImageMutation.isPending}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            /* Grid View */
            <Grid container spacing={isMobile ? 1 : 2}>
              {data?.images.map((image, index) => (
                <Grid item {...getGridColumns()} key={image.filename}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    border: selectedImages.has(image.filename) ? '2px solid' : 'none',
                    borderColor: 'primary.main',
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: 3,
                    },
                  }}
                >
                  <Box position="absolute" top={4} left={4} zIndex={2}>
                    <Checkbox
                      checked={selectedImages.has(image.filename)}
                      onChange={() => handleSelectImage(image.filename)}
                      onClick={(e) => e.stopPropagation()}
                      size="small"
                      sx={{ 
                        bgcolor: 'rgba(255, 255, 255, 0.95)',
                        p: 0.5,
                        borderRadius: 1,
                        '& .MuiSvgIcon-root': {
                          fontSize: isMobile ? 18 : 20,
                        },
                      }}
                    />
                  </Box>
                  <Box position="absolute" top={4} right={4} zIndex={2}>
                    <Chip
                      icon={getTypeIcon(image.type)}
                      label={image.type}
                      size="small"
                      color={getTypeColor(image.type) as any}
                      sx={{ 
                        bgcolor: 'rgba(255, 255, 255, 0.95)',
                        height: isMobile ? 20 : 24,
                        fontSize: isMobile ? '0.65rem' : '0.75rem',
                        '& .MuiChip-icon': {
                          fontSize: isMobile ? 14 : 16,
                        },
                      }}
                    />
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      height: getThumbnailHeight(),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.100',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                    onClick={() => handleImageClick(image.url, index)}
                  >
                    {image.isVideo ? (
                      videoLoadErrors.has(image.url) ? (
                        <Box
                          sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'grey.200',
                            color: 'text.secondary',
                            cursor: 'pointer',
                          }}
                        >
                          <VideoIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', textAlign: 'center', px: 1 }}>
                            Video (Codec Issue)
                          </Typography>
                          <Typography variant="caption" sx={{ fontSize: '0.65rem', textAlign: 'center', px: 1, mt: 0.5, opacity: 0.7 }}>
                            Click to view
                          </Typography>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                          }}
                        >
                          <Box
                            component="video"
                            src={image.url}
                            preload="metadata"
                            muted
                            playsInline
                            crossOrigin="anonymous"
                            width="100%"
                            height="100%"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              width: 'auto',
                              height: 'auto',
                              objectFit: 'contain',
                              cursor: 'pointer',
                              filter: blurEnabled ? 'blur(8px)' : 'none',
                              display: 'block',
                            }}
                            onError={(e: any) => {
                              // Silently handle video load errors - mark as failed
                              setVideoLoadErrors(prev => new Set(prev).add(image.url));
                            }}
                            onLoadedMetadata={(e: any) => {
                              const video = e.target as HTMLVideoElement;
                              try {
                                // Seek to first frame to show thumbnail
                                if (video.duration > 0) {
                                  video.currentTime = 0.01;
                                }
                              } catch (err) {
                                // Ignore seek errors
                              }
                            }}
                          />
                        </Box>
                      )
                    ) : (
                      <Box
                        component="img"
                      src={image.url}
                      alt={image.filename}
                      sx={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                        cursor: 'pointer',
                        filter: blurEnabled ? 'blur(8px)' : 'none',
                        transition: 'filter 0.3s ease',
                        '&:hover': {
                          filter: blurEnabled ? 'blur(4px)' : 'none',
                        },
                      }}
                      onError={(e: any) => {
                        e.target.src = '/placeholder-image.png';
                      }}
                    />
                    )}
                  </Box>
                  {!isMobile && (
                    <CardContent sx={{ flexGrow: 1, pt: 1, pb: 0.5, px: 1 }}>
                      <Typography variant="caption" color="text.secondary" noWrap fontSize="0.7rem" display="block">
                        {image.filename.length > 25 ? `${image.filename.substring(0, 25)}...` : image.filename}
                      </Typography>
                      {image.deviceId && image.deviceId !== 'unknown' && (
                        <Typography variant="caption" color="primary" fontSize="0.65rem" display="block" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                          Device: {image.deviceId}
                        </Typography>
                      )}
                      {image.userId && (
                        <Typography variant="caption" color="text.secondary" fontSize="0.65rem" display="block">
                          User: {image.userId.substring(0, 12)}...
                        </Typography>
                      )}
                      {image.userEmail && (
                        <Typography variant="caption" color="text.secondary" fontSize="0.65rem" display="block">
                          {image.userEmail}
                        </Typography>
                      )}
                      {image.platform && (
                        <Chip
                          label={image.platform}
                          size="small"
                          variant="outlined"
                          sx={{ mt: 0.5, fontSize: '0.6rem', height: 16 }}
                        />
                      )}
                      {image.templateId && (
                        <Typography variant="caption" color="text.secondary" fontSize="0.65rem" display="block" sx={{ mt: 0.5, fontFamily: 'monospace' }}>
                          Template: {image.templateId}
                        </Typography>
                      )}
                      {(image.country || image.countryCode) && (
                        <Chip
                          label={image.country || image.countryCode || 'Unknown'}
                          size="small"
                          variant="outlined"
                          color="secondary"
                          sx={{ mt: 0.5, fontSize: '0.6rem', height: 16 }}
                        />
                      )}
                      <Typography variant="caption" color="text.secondary" fontSize="0.65rem" sx={{ mt: 0.5, display: 'block' }}>
                        {image.sizeFormatted}
                      </Typography>
                    </CardContent>
                  )}
                  {/* Show country on mobile too */}
                  {isMobile && (image.country || image.countryCode) && (
                    <Box sx={{ px: 1, pb: 0.5 }}>
                      <Chip
                        label={image.country || image.countryCode || 'Unknown'}
                        size="small"
                        variant="outlined"
                        color="secondary"
                        sx={{ fontSize: '0.6rem', height: 16 }}
                      />
                    </Box>
                  )}
                  <CardActions sx={{ p: isMobile ? 0.5 : 0.75, justifyContent: 'center', minHeight: 'auto' }}>
                    <Tooltip title="View">
                      <IconButton
                        size="small"
                        onClick={() => handleImageClick(image.url, index)}
                        sx={{ p: isMobile ? 0.5 : 0.75 }}
                      >
                        <FullscreenIcon fontSize={isMobile ? 'small' : 'medium'} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Metadata">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedImageMetadata(image);
                          setMetadataDialogOpen(true);
                        }}
                        sx={{ p: isMobile ? 0.5 : 0.75 }}
                      >
                        <InfoIcon fontSize={isMobile ? 'small' : 'medium'} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(image.filename);
                        }}
                        disabled={deleteImageMutation.isPending}
                        sx={{ p: isMobile ? 0.5 : 0.75 }}
                      >
                        <DeleteIcon fontSize={isMobile ? 'small' : 'medium'} />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
          )}

          {data && data.totalPages > 1 && (
            <Box display="flex" justifyContent="center" alignItems="center" mt={3} gap={2} flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={<NavigateBeforeIcon />}
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1 || isLoading}
                size={isMobile ? 'small' : 'medium'}
              >
                Previous
              </Button>
              <Pagination
                count={data.totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                size={isMobile ? 'small' : 'medium'}
                siblingCount={isMobile ? 0 : 1}
              />
              <Button
                variant="outlined"
                endIcon={<NavigateNextIcon />}
                onClick={() => setPage(Math.min(data.totalPages, page + 1))}
                disabled={page === data.totalPages || isLoading}
                size={isMobile ? 'small' : 'medium'}
              >
                Next
              </Button>
            </Box>
          )}

          <Box mt={2} textAlign="center">
            <Typography variant={isMobile ? 'caption' : 'body2'} color="text.secondary">
              Showing {data?.images.length || 0} of {data?.total || 0} images
              {data && data.totalPages > 1 && ` (Page ${page} of ${data.totalPages})`}
            </Typography>
          </Box>
        </>
      )}

      {/* Delete Single Image Dialog */}
      <Dialog
        open={deleteSingleDialogOpen}
        onClose={() => setDeleteSingleDialogOpen(false)}
      >
        <DialogTitle>Delete Image</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this image? This action cannot be undone.
            <br />
            <strong>{imageToDelete}</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteSingleDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteImageMutation.isPending}
          >
            {deleteImageMutation.isPending ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Multiple Images Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Selected Images</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selectedImages.size} image(s)? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteMultiple}
            color="error"
            variant="contained"
            disabled={deleteMultipleMutation.isPending}
          >
            {deleteMultipleMutation.isPending ? <CircularProgress size={20} /> : 'Delete All'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick Delete Confirmation Dialog */}
      <Dialog
        open={quickDeleteDialogOpen}
        onClose={() => setQuickDeleteDialogOpen(false)}
      >
        <DialogTitle>Bulk Delete Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete all output images older than <strong>{quickDeleteMinutes !== null ? formatQuickDeleteLabel(quickDeleteMinutes) : ''}</strong>?
            <br /><br />
            This will permanently delete all matching files from the server. This action cannot be undone.
          </DialogContentText>
          {deleteOlderThanMutation.isSuccess && (
            <Alert severity="success" sx={{ mt: 1 }}>
              {(deleteOlderThanMutation.data as any)?.totalDeleted ?? 0} file(s) deleted successfully.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuickDeleteDialogOpen(false)} disabled={deleteOlderThanMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleQuickDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteOlderThanMutation.isPending}
          >
            {deleteOlderThanMutation.isPending ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            m: isMobile ? 0 : 2,
            height: isMobile ? '100%' : 'auto',
            maxHeight: isMobile ? '100%' : '90vh',
          },
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1,
        }}>
          <Box display="flex" alignItems="center" gap={2} flex={1}>
            {data && (
              <Typography variant="body2" color="text.secondary">
                {previewIndex + 1} / {data.images.length}
                {data.totalPages > 1 && ` (Page ${page})`}
              </Typography>
            )}
            {data && data.images[previewIndex] && (
              <Chip
                icon={getTypeIcon(data.images[previewIndex].type)}
                label={data.images[previewIndex].type}
                size="small"
                color={getTypeColor(data.images[previewIndex].type) as any}
              />
            )}
          </Box>
          <IconButton
            onClick={() => setPreviewImage(null)}
            size="small"
            sx={{ ml: 'auto' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: isMobile ? 1 : 2,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: isMobile ? 'calc(100vh - 120px)' : '60vh',
            overflow: 'hidden',
          }}
          ref={previewContainerRef}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {data && data.images[previewIndex] && (
            <>
              {previewIndex > 0 && (
                <Fab
                  color="primary"
                  aria-label="previous"
                  onClick={handlePreviousImage}
                  sx={{
                    position: 'absolute',
                    left: isMobile ? 8 : 16,
                    zIndex: 1,
                  }}
                  size={isMobile ? 'small' : 'medium'}
                >
                  <NavigateBeforeIcon />
                </Fab>
              )}
              {data.images[previewIndex].isVideo ? (
                previewVideoError && videoPlaybackMethod === 'error' ? (
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: '100%',
                      maxHeight: isMobile ? 'calc(100vh - 140px)' : '70vh',
                      minHeight: '300px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#000',
                      borderRadius: 1,
                      p: 4,
                      color: 'white',
                      textAlign: 'center',
                    }}
                  >
                    <VideoIcon sx={{ fontSize: 64, mb: 2, opacity: 0.7 }} />
                    <Typography variant="h6" gutterBottom>
                      Video Cannot Be Played in Browser
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 3, maxWidth: '600px', color: 'rgba(255, 255, 255, 0.8)' }}>
                      This video uses a codec that is not supported by web browsers (DEMUXER_ERROR_NO_SUPPORTED_STREAMS).
                      The video file is valid, but browsers cannot decode it directly.
                    </Typography>
                    <Box display="flex" gap={2} flexWrap="wrap" justifyContent="center">
                      <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={() => {
                          if (previewImage) {
                            const link = document.createElement('a');
                            link.href = previewImage;
                            link.download = data.images[previewIndex].filename;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }
                        }}
                        sx={{ mb: 1 }}
                      >
                        Download Video
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<PlayArrowIcon />}
                        onClick={() => {
                          if (previewImage) {
                            window.open(previewImage, '_blank');
                          }
                        }}
                        sx={{ mb: 1, color: 'white', borderColor: 'white', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255, 255, 255, 0.1)' } }}
                      >
                        Open in New Tab
                      </Button>
                    </Box>
                    <Typography variant="caption" sx={{ mt: 3, color: 'rgba(255, 255, 255, 0.6)' }}>
                      Tip: Download the video and play it using VLC Media Player, Windows Media Player, or QuickTime Player
                    </Typography>
                  </Box>
                ) : videoPlaybackMethod === 'iframe' ? (
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: '100%',
                      maxHeight: isMobile ? 'calc(100vh - 140px)' : '70vh',
                      minHeight: '300px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#000',
                      borderRadius: 1,
                      overflow: 'hidden',
                      position: 'relative',
                    }}
                  >
                    <iframe
                      src={previewImage || ''}
                      style={{
                        width: '100%',
                        height: '100%',
                        minHeight: '300px',
                        border: 'none',
                      }}
                      title="Video Player"
                      allow="autoplay; encrypted-media"
                      onError={() => {
                        setVideoPlaybackMethod('object');
                      }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setVideoPlaybackMethod('video');
                        setPreviewVideoError(false);
                      }}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        borderColor: 'white',
                        '&:hover': { borderColor: 'white', bgcolor: 'rgba(0, 0, 0, 0.9)' }
                      }}
                    >
                      Try Video Element
                    </Button>
                  </Box>
                ) : videoPlaybackMethod === 'object' ? (
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: '100%',
                      maxHeight: isMobile ? 'calc(100vh - 140px)' : '70vh',
                      minHeight: '300px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#000',
                      borderRadius: 1,
                      overflow: 'hidden',
                      position: 'relative',
                    }}
                  >
                    <Box
                      component="object"
                      data={previewImage || ''}
                      type="video/mp4"
                      style={{
                        width: '100%',
                        height: '100%',
                        minHeight: '300px',
                      }}
                    >
                      <Box
                        component="embed"
                        src={previewImage || ''}
                        type="video/mp4"
                        style={{
                          width: '100%',
                          height: '100%',
                          minHeight: '300px',
                        }}
                      />
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setVideoPlaybackMethod('error');
                      }}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        borderColor: 'white',
                        '&:hover': { borderColor: 'white', bgcolor: 'rgba(0, 0, 0, 0.9)' }
                      }}
                    >
                      Show Options
                    </Button>
                  </Box>
                ) : videoPlaybackMethod === 'video' ? (
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: '100%',
                      maxHeight: isMobile ? 'calc(100vh - 140px)' : '70vh',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#000',
                      borderRadius: 1,
                      overflow: 'hidden',
                      filter: blurEnabled && !previewBlurDisabled ? 'blur(4px)' : 'none',
                      transition: 'filter 0.3s ease',
                      position: 'relative',
                    }}
                  >
                    {videoCodecIssue && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          zIndex: 10,
                          bgcolor: 'rgba(0, 0, 0, 0.8)',
                          color: 'white',
                          p: 3,
                          borderRadius: 2,
                          textAlign: 'center',
                          maxWidth: '80%',
                        }}
                      >
                        <Typography variant="h6" gutterBottom>
                          Video Codec Issue
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          This video uses an unsupported codec (MPEG-4 part 2) and cannot be displayed in the browser.
                        </Typography>
                        <Box display="flex" gap={1} justifyContent="center" flexWrap="wrap" sx={{ mt: 2 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setVideoPlaybackMethod('iframe');
                              setVideoCodecIssue(false);
                            }}
                            sx={{ color: 'white', borderColor: 'white', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255, 255, 255, 0.1)' } }}
                          >
                            Try iFrame
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setVideoPlaybackMethod('object');
                              setVideoCodecIssue(false);
                            }}
                            sx={{ color: 'white', borderColor: 'white', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255, 255, 255, 0.1)' } }}
                          >
                            Try Object Tag
                          </Button>
                        </Box>
                      </Box>
                    )}
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        const currentMethod = videoPlaybackMethodRef.current;
                        if (currentMethod === 'video') {
                          setVideoPlaybackMethod('iframe');
                        } else if (currentMethod === 'iframe') {
                          setVideoPlaybackMethod('object');
                        } else if (currentMethod === 'object') {
                          setVideoPlaybackMethod('video');
                        } else {
                          setVideoPlaybackMethod('video');
                        }
                        setPreviewVideoError(false);
                        setVideoCodecIssue(false);
                      }}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 5,
                        bgcolor: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        borderColor: 'white',
                        '&:hover': { borderColor: 'white', bgcolor: 'rgba(0, 0, 0, 0.9)' }
                      }}
                    >
                      {(() => {
                        const method = videoPlaybackMethodRef.current;
                        if (method === 'video') return 'Try iFrame';
                        if (method === 'iframe') return 'Try Object';
                        return 'Try Video';
                      })()}
                    </Button>
                    <Box
                      component="video"
                      ref={videoRef}
                      src={previewImage || ''}
                      controls
                      preload="auto"
                      playsInline
                      crossOrigin="anonymous"
                      muted={false}
                      style={{
                        width: '100%',
                        height: 'auto',
                        maxWidth: '100%',
                        maxHeight: isMobile ? 'calc(100vh - 140px)' : '70vh',
                        minHeight: '300px',
                        objectFit: 'contain',
                        display: 'block',
                        backgroundColor: '#000',
                      }}
                    onError={(e: any) => {
                      if (e.target?.error) {
                        const errorCode = e.target.error.code;
                        const errorMessage = e.target.error.message;
                        console.error('Video load error in preview - Error code:', errorCode, 'Message:', errorMessage);
                        
                        const currentMethod = videoPlaybackMethodRef.current;
                        // Try iframe method if video element fails
                        if (currentMethod === 'video') {
                          console.log('Trying iframe playback method...');
                          setVideoPlaybackMethod('iframe');
                          setPreviewVideoError(false);
                        } else if (currentMethod === 'iframe') {
                          // Try object/embed method if iframe fails
                          console.log('Trying object/embed playback method...');
                          setVideoPlaybackMethod('object');
                          setPreviewVideoError(false);
                        } else if (currentMethod === 'object') {
                          // All methods failed
                          setPreviewVideoError(true);
                          setVideoPlaybackMethod('error');
                        } else {
                          // Already in error state
                          setPreviewVideoError(true);
                        }
                      } else {
                        setPreviewVideoError(true);
                        setVideoPlaybackMethod('error');
                      }
                    }}
                      onLoadedMetadata={(e: any) => {
                        const video = e.target as HTMLVideoElement;
                        setPreviewVideoError(false);
                        // Set dimensions if available
                        if (video.videoWidth > 0 && video.videoHeight > 0) {
                          setVideoDimensions({ width: video.videoWidth, height: video.videoHeight });
                          // Try to show first frame
                          try {
                            if (video.duration > 0) {
                              video.currentTime = 0.01;
                            }
                          } catch (err) {
                            // Ignore seek errors
                          }
                        }
                      }}
                      onCanPlay={(e: any) => {
                        setPreviewVideoError(false);
                      }}
                      onPlaying={(e: any) => {
                        const video = e.target as HTMLVideoElement;
                        // Check for codec issues
                        if (video.videoWidth === 0 && video.videoHeight === 0 && video.readyState >= 2) {
                          setVideoCodecIssue(true);
                        } else {
                          setVideoCodecIssue(false);
                        }
                      }}
                      onTimeUpdate={(e: any) => {
                        const video = e.target as HTMLVideoElement;
                        // Check if video is playing but not rendering (codec issue)
                        if (video.videoWidth === 0 && video.videoHeight === 0 && video.readyState >= 2 && !video.paused) {
                          setVideoCodecIssue(true);
                        }
                      }}
                    />
                  </Box>
                ) : null
              ) : (
                <Box
                  component="img"
                src={previewImage || ''}
                alt={data.images[previewIndex].filename}
                sx={{
                  maxWidth: '100%',
                  maxHeight: isMobile ? 'calc(100vh - 140px)' : '70vh',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: 1,
                  filter: blurEnabled && !previewBlurDisabled ? 'blur(4px)' : 'none',
                  transition: 'filter 0.3s ease',
                }}
                onError={(e: any) => {
                  e.target.src = '/placeholder-image.png';
                }}
              />
              )}
              {blurEnabled && !previewBlurDisabled && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setPreviewBlurDisabled(true)}
                    sx={{
                      backdropFilter: 'blur(10px)',
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                      boxShadow: 3,
                    }}
                  >
                    Click to View
                  </Button>
                </Box>
              )}
              {(previewIndex < data.images.length - 1 || (data && page < data.totalPages)) && (
                <Fab
                  color="primary"
                  aria-label="next"
                  onClick={handleNextImage}
                  sx={{
                    position: 'absolute',
                    right: isMobile ? 8 : 16,
                    zIndex: 1,
                  }}
                  size={isMobile ? 'small' : 'medium'}
                >
                  <NavigateNextIcon />
                </Fab>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          justifyContent: 'space-between',
          px: isMobile ? 1 : 2,
          pb: isMobile ? 1 : 2,
        }}>
          <Box>
            {data && data.images[previewIndex] && (
              <>
                <Typography variant="caption" display="block" color="text.secondary">
                  {data.images[previewIndex].filename}
                </Typography>
                {data.images[previewIndex].deviceId && data.images[previewIndex].deviceId !== 'unknown' && (
                  <Typography variant="caption" display="block" color="primary" sx={{ fontFamily: 'monospace' }}>
                    Device ID: {data.images[previewIndex].deviceId}
                  </Typography>
                )}
                {data.images[previewIndex].userId && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    User ID: {data.images[previewIndex].userId}
                  </Typography>
                )}
                {data.images[previewIndex].userEmail && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    Email: {data.images[previewIndex].userEmail}
                  </Typography>
                )}
                {data.images[previewIndex].platform && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    Platform: {data.images[previewIndex].platform}
                  </Typography>
                )}
                {data.images[previewIndex].templateId && (
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    Template: {data.images[previewIndex].templateId}
                  </Typography>
                )}
                {data.images[previewIndex].country && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    Country: {data.images[previewIndex].country} {data.images[previewIndex].countryCode && `(${data.images[previewIndex].countryCode})`}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  {data.images[previewIndex].sizeFormatted} • {formatDate(data.images[previewIndex].createdFormatted)}
                </Typography>
              </>
            )}
          </Box>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<NavigateBeforeIcon />}
              onClick={handlePreviousImage}
              disabled={previewIndex === 0 && page === 1}
              size={isMobile ? 'small' : 'medium'}
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              endIcon={<NavigateNextIcon />}
              onClick={handleNextImage}
              disabled={previewIndex === (data?.images.length || 0) - 1 && page === (data?.totalPages || 1)}
              size={isMobile ? 'small' : 'medium'}
            >
              Next
            </Button>
            <Button
              variant="outlined"
              startIcon={<InfoIcon />}
              onClick={() => {
                if (data && data.images[previewIndex]) {
                  setSelectedImageMetadata(data.images[previewIndex]);
                  setMetadataDialogOpen(true);
                }
              }}
              size={isMobile ? 'small' : 'medium'}
            >
              Metadata
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => {
                if (data && data.images[previewIndex]) {
                  handleDeleteClick(data.images[previewIndex].filename);
                }
              }}
              size={isMobile ? 'small' : 'medium'}
            >
              Delete
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Metadata Details Dialog */}
      <Dialog
        open={metadataDialogOpen}
        onClose={() => setMetadataDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Image Metadata
          {selectedImageMetadata && (
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
              {selectedImageMetadata.filename}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedImageMetadata && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                Device Information
              </Typography>
              <Box sx={{ pl: 2, mb: 2 }}>
                <Typography variant="body2">
                  <strong>Device ID:</strong>{' '}
                  {selectedImageMetadata.deviceId && selectedImageMetadata.deviceId !== 'unknown' ? (
                    <span style={{ fontFamily: 'monospace' }}>{selectedImageMetadata.deviceId}</span>
                  ) : (
                    <span style={{ color: '#999' }}>N/A</span>
                  )}
                </Typography>
                {selectedImageMetadata.operationType && (
                  <Typography variant="body2">
                    <strong>Operation:</strong> {selectedImageMetadata.operationType}
                  </Typography>
                )}
                {selectedImageMetadata.platform && (
                  <Typography variant="body2">
                    <strong>Platform:</strong> {selectedImageMetadata.platform}
                  </Typography>
                )}
              </Box>

              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                User Information
              </Typography>
              <Box sx={{ pl: 2, mb: 2 }}>
                {selectedImageMetadata.userId ? (
                  <Typography variant="body2">
                    <strong>User ID:</strong>{' '}
                    <span style={{ fontFamily: 'monospace' }}>{selectedImageMetadata.userId}</span>
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    User ID: N/A
                  </Typography>
                )}
                {selectedImageMetadata.userEmail ? (
                  <Typography variant="body2">
                    <strong>Email:</strong> {selectedImageMetadata.userEmail}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Email: N/A
                  </Typography>
                )}
              </Box>

              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                File Information
              </Typography>
              <Box sx={{ pl: 2, mb: 2 }}>
                <Typography variant="body2">
                  <strong>Size:</strong> {selectedImageMetadata.sizeFormatted}
                </Typography>
                <Typography variant="body2">
                  <strong>Type:</strong> {selectedImageMetadata.type}
                </Typography>
                <Typography variant="body2">
                  <strong>Created:</strong> {formatDate(selectedImageMetadata.createdFormatted)}
                </Typography>
                <Typography variant="body2">
                  <strong>Modified:</strong> {formatDate(selectedImageMetadata.modifiedFormatted)}
                </Typography>
                {selectedImageMetadata.timestamp && (
                  <Typography variant="body2">
                    <strong>Timestamp:</strong> {new Date(selectedImageMetadata.timestamp).toLocaleString()}
                  </Typography>
                )}
              </Box>

              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                Request Information
              </Typography>
              <Box sx={{ pl: 2, mb: 2 }}>
                {selectedImageMetadata.metadata?.ipAddress && (
                  <Typography variant="body2">
                    <strong>IP Address:</strong> {selectedImageMetadata.metadata.ipAddress}
                  </Typography>
                )}
                {selectedImageMetadata.metadata?.userAgent && (
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    <strong>User Agent:</strong> {selectedImageMetadata.metadata.userAgent}
                  </Typography>
                )}
                {selectedImageMetadata.metadata?.timestamp && (
                  <Typography variant="body2">
                    <strong>Request Time:</strong> {new Date(selectedImageMetadata.metadata.timestamp).toLocaleString()}
                  </Typography>
                )}
                {selectedImageMetadata.templateId && (
                  <Typography variant="body2">
                    <strong>Template ID:</strong>{' '}
                    <span style={{ fontFamily: 'monospace' }}>{selectedImageMetadata.templateId}</span>
                  </Typography>
                )}
                {selectedImageMetadata.ipAddress && (
                  <Typography variant="body2">
                    <strong>IP Address:</strong>{' '}
                    <span style={{ fontFamily: 'monospace' }}>{selectedImageMetadata.ipAddress}</span>
                  </Typography>
                )}
                {selectedImageMetadata.country && (
                  <Typography variant="body2">
                    <strong>Country:</strong> {selectedImageMetadata.country}
                    {selectedImageMetadata.countryCode && ` (${selectedImageMetadata.countryCode})`}
                  </Typography>
                )}
              </Box>

              {selectedImageMetadata.metadata && Object.keys(selectedImageMetadata.metadata).length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                    Full Metadata (JSON)
                  </Typography>
                  <Box sx={{ pl: 2, mb: 2 }}>
                    <pre style={{ 
                      background: '#f5f5f5', 
                      padding: '8px', 
                      borderRadius: '4px', 
                      fontSize: '0.75rem',
                      overflow: 'auto',
                      maxHeight: '200px',
                      margin: 0
                    }}>
                      {JSON.stringify(selectedImageMetadata.metadata, null, 2)}
                    </pre>
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMetadataDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OutputImages;

